/** @jsxRuntime automatic */
/** @jsxImportSource react */
/**
 * Analytics › Overview (contract section 5): four tiles (sessions, users, events, engaged share) with their change on the
 * window just before, and the daily trend of sessions, all from GET /v1/admin/usage/sessions with the lens (its totals,
 * `previous` and `daily`).
 */
import { api } from '../../lib/api';
import { delta, deltaText, engagedShare, normalizeSessions, type SessionsView } from '../../lib/usage';
import { Grid, Kpi, Panel, Skeleton, Stack } from '../../ui';
import { count, percent } from '../../ui/format';
import { useLoad } from '../pulse/common';
import { Trend, UsageError, type SectionProps } from './parts';

export function UsageOverview({ lens, query, lensKey }: SectionProps) {
  const sessions = useLoad(() => api.usageSessions(query).then(normalizeSessions), `overview-sessions|${lensKey}`);
  const data: SessionsView | null = sessions.load.state === 'ready' ? sessions.load.data : null;
  const loading = sessions.load.state === 'loading';
  const previous = data?.previous ?? null;
  const engaged = data ? engagedShare(data) : null;
  const engagedBefore = previous && previous.count !== null && previous.count > 0 && previous.engaged !== null ? previous.engaged / previous.count : null;

  if (sessions.load.state === 'failed') {
    return <Panel title="At a glance"><UsageError title="The overview could not be loaded" error={sessions.load.error} onRetry={sessions.reload} retrying={sessions.reloading} /></Panel>;
  }

  return (
    <Stack>
      <Panel title="At a glance" description={`Last ${lens.window} days against the ${lens.window} days before`}>
        <Grid cols={4} label="Overview">
          <Kpi label="Sessions" loading={loading} value={data ? count(data.count) : '—'} sub={data ? deltaText(delta(data.count, previous?.count), lens.window) : undefined} />
          <Kpi label="Users" loading={loading} value={data ? count(data.users) : '—'} sub={data ? deltaText(delta(data.users, previous?.users), lens.window) : undefined} />
          <Kpi label="Events" loading={loading} value={data ? count(data.events) : '—'} sub={data ? deltaText(delta(data.events, previous?.events), lens.window) : undefined} />
          <Kpi label="Engaged share" loading={loading} value={engaged === null ? '—' : percent(engaged, 1)} sub={data ? `${count(data.engaged)} of ${count(data.count)} sessions 10 s or longer, or 2+ screens; ${deltaText(delta(engaged, engagedBefore), lens.window)}` : undefined} />
        </Grid>
      </Panel>
      <Panel title="Daily trend" description={`Sessions per day, last ${lens.window} days`}>
        {loading ? <Skeleton size="xl" /> : <Trend values={(data?.daily ?? []).map((point) => point.sessions)} days={(data?.daily ?? []).map((point) => point.day)} label="Sessions per day" />}
      </Panel>
    </Stack>
  );
}
